import React, { useState } from 'react';
import { Calendar, Gift, Package, Wine } from 'lucide-react';
import { PortalLoyaltyPageId, PortalPreviewModalId, PortalPreviewMode, PortalPreviewPageId, PortalThemeColors, PortalThemeFieldId, portalLoyaltyPages, portalPreviewModals, portalPreviewPages, portalThemeFields } from '../types/portalTheme';
interface PortalThemePreviewProps {
  colors: PortalThemeColors;
  primaryGradient: string;
  /** Renders the paused subscription treatment. */
  paused?: boolean;
  mode: PortalPreviewMode;
  page: PortalPreviewPageId;
  loyaltyPage: PortalLoyaltyPageId;
  modal: PortalPreviewModalId;
  inspectMode: boolean;
  onInspect: (field: PortalThemeFieldId) => void;
  /** When set, the preview stands in for this member's real data. */
  loadedSubscriptionId: string | null;
}
interface RegionProps {
  field: PortalThemeFieldId;
  inspect: boolean;
  onInspect: (field: PortalThemeFieldId) => void;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}
function Region({
  field,
  inspect,
  onInspect,
  className = '',
  style,
  children
}: RegionProps) {
  const [hovered, setHovered] = useState(false);
  if (!inspect) {
    return <div className={className} style={style} data-id="element-4265" data-mp="3q3h6nozsqyxr">
        {children}
      </div>;
  }
  const label = portalThemeFields.find(item => item.id === field)?.label ?? field;
  return <div className={`relative ${className}`} style={{
    ...style,
    outline: hovered ? '2px solid #0EA5E9' : undefined,
    outlineOffset: 1,
    cursor: 'pointer'
  }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onClick={event => {
    event.stopPropagation();
    onInspect(field);
  }} data-id="element-4266" data-mp="3l44ecoq5ozns">
      {children}
      {hovered && <span className="absolute -top-2.5 left-2 z-30 px-1.5 py-0.5 rounded bg-[#0EA5E9] text-white text-[10px] font-semibold whitespace-nowrap shadow-sm" data-id="element-4267" data-mp="326bmchh2ma56">
          {label}
        </span>}
    </div>;
}
export function PortalThemePreview({
  colors,
  primaryGradient,
  paused = false,
  mode,
  page,
  loyaltyPage,
  modal,
  inspectMode,
  onInspect,
  loadedSubscriptionId
}: PortalThemePreviewProps) {
  const isLoyaltyOnly = mode === 'loyalty';
  const heroFill = primaryGradient.trim() !== '' ? primaryGradient : colors.heroBackground;
  const region = (field: PortalThemeFieldId, className: string, style: React.CSSProperties, children: React.ReactNode) => <Region field={field} inspect={inspectMode} onInspect={onInspect} className={className} style={style} data-id="element-4268" data-mp="2psy5m3ve01ei">
      {children}
    </Region>;
  const filledButton = (label: string, className = '') => region('primary', `inline-block rounded-md px-4 py-2 text-[13px] font-semibold ${className}`, {
    backgroundColor: colors.primary,
    color: colors.buttonText
  }, <Region field="buttonText" inspect={inspectMode} onInspect={onInspect} data-id="element-4269" data-mp="38kh7g7dod9ey">
        {label}
      </Region>);
  const outlineButton = (label: string, className = '') => region('outlineButton', `inline-block rounded-md px-4 py-2 text-[13px] font-semibold ${className}`, {
    border: `1px solid ${colors.outlineButton}`,
    color: colors.text
  }, label);
  const heroButton = (label: string) => <Region field="buttonText" inspect={inspectMode} onInspect={onInspect} className="inline-block rounded-md px-3.5 py-2 text-[12px] font-semibold" style={{
    border: `1px solid ${colors.buttonText}`,
    color: colors.buttonText
  }} data-id="element-4270" data-mp="a94248lvoqvd">
      {label}
    </Region>;
  const tile = (name: string, meta: string) => <div key={name} className="rounded-lg overflow-hidden" style={{
    border: `1px solid ${colors.border}`
  }} data-id="element-4271" data-mp="3soemr0ftc86a">
      {region('productTile', 'h-20 flex items-center justify-center', {
      backgroundColor: colors.productTile
    }, <Wine className="w-7 h-7" style={{
      color: colors.primary
    }} data-id="element-4272" data-mp="5cnfxjy8omkx" />)}
      <div className="p-2.5" data-id="element-4273" data-mp="iszqqhuepql">
        <div className="text-[12px] font-semibold" style={{
        color: colors.text
      }} data-id="element-4274" data-mp="38zw3t0lehr01">
          {name}
        </div>
        <div className="text-[11px] opacity-70" style={{
        color: colors.text
      }} data-id="element-4275" data-mp="34z3sm1syxc14">
          {meta}
        </div>
      </div>
    </div>;
  const heading = (label: string) => <Region field="text" inspect={inspectMode} onInspect={onInspect} data-id="element-4276" data-mp="3mmczsrwfkndt">
      <h3 className="text-[14px] font-semibold mb-3" style={{
      color: colors.text
    }} data-id="element-4277" data-mp="3mkjubfxk097e">
        {label}
      </h3>
    </Region>;
  const row = (label: string, value: string) => <div key={label} className="flex items-center justify-between py-2.5" style={{
    borderBottom: `1px solid ${colors.border}`
  }} data-id="element-4278" data-mp="8mjz8w9gree">
      <span className="text-[12px] opacity-70" style={{
      color: colors.text
    }} data-id="element-4279" data-mp="97q32cvfh6sw">
        {label}
      </span>
      <span className="text-[12px] font-medium" style={{
      color: colors.text
    }} data-id="element-4280" data-mp="e6b7yplmkeno">
        {value}
      </span>
    </div>;
  const statCard = (label: string, value: string, meta: string) => <Region key={label} field="buttonText" inspect={inspectMode} onInspect={onInspect} className="rounded-lg p-3" style={{
    border: `1px solid ${colors.buttonText}`
  }} data-id="element-4281" data-mp="2fyfuflkcujr0">
      <div className="text-[10px] uppercase tracking-wider" style={{
      color: colors.buttonText,
      opacity: 0.75
    }} data-id="element-4282" data-mp="2kz3v6yydyeyf">
        {label}
      </div>
      <div className="text-[15px] font-semibold mt-0.5" style={{
      color: colors.buttonText
    }} data-id="element-4283" data-mp="28m9jx2l6124w">
        {value}
      </div>
      <div className="text-[11px]" style={{
      color: colors.buttonText,
      opacity: 0.75
    }} data-id="element-4284" data-mp="23luagm408es7">
        {meta}
      </div>
    </Region>;
  const tabBody = () => {
    switch (page) {
      case 'upcoming':
        return <>
            {heading('Upcoming deliveries')}
            <div className="space-y-2.5" data-id="element-4285" data-mp="2re2h7979p9if">
              {[['Delivery #4821', 'Ships 14 Aug 2026'], ['Delivery #4822', 'Ships 14 Nov 2026'], ['Delivery #4823', 'Ships 14 Feb 2027']].map(([id, when]) => <div key={id} className="rounded-lg p-3 flex items-center justify-between" style={{
              border: `1px solid ${colors.border}`
            }} data-id="element-4286" data-mp="2hejysx709vsh">
                  <div data-id="element-4287" data-mp="15zoil6spnm9t">
                    <div className="text-[13px] font-semibold" style={{
                  color: colors.text
                }} data-id="element-4288" data-mp="15zertgzudb32">
                      {id}
                    </div>
                    <div className="text-[11px] opacity-70" style={{
                  color: colors.text
                }} data-id="element-4289" data-mp="22m6rtsgem2c">
                      {when}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4290" data-mp="3p5zajz3csgt4">
                    {outlineButton('Skip')}
                    {filledButton('Ship now')}
                  </div>
                </div>)}
            </div>
          </>;
      case 'rewards':
        return <>
            {heading('Rewards')}
            <div className="rounded-lg p-3 mb-3 flex items-center gap-3" style={{
            border: `1px solid ${colors.border}`
          }} data-id="element-4291" data-mp="31iitjsljg8hh">
              <Region field="primary" inspect={inspectMode} onInspect={onInspect} data-id="element-4292" data-mp="36e6hk4oaavle">
                <Gift className="w-6 h-6" style={{
                color: colors.primary
              }} data-id="element-4293" data-mp="2rk1wqezx4zni" />
              </Region>
              <div data-id="element-4294" data-mp="2cwl6pgqxfexp">
                <div className="text-[13px] font-semibold" style={{
                color: colors.text
              }} data-id="element-4295" data-mp="2cw1odyxd6oj5">
                  2,450 points
                </div>
                <div className="text-[11px] opacity-70" style={{
                color: colors.text
              }} data-id="element-4296" data-mp="2mur3xl1sfww8">
                  Sommelier tier
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5" data-id="element-4297" data-mp="2vv75uq2vuvyz">
              {[['Free freight', '500 pts'], ['Bonus bottle', '1,500 pts']].map(([name, cost]) => tile(name, cost))}
            </div>
          </>;
      case 'past':
        return <>
            {heading('Past deliveries')}
            <div data-id="element-4298" data-mp="35n34q3csmq49">
              {[['14 May 2026', '6 bottles · $228.00'], ['14 Feb 2026', '6 bottles · $228.00'], ['14 Nov 2025', '6 bottles · $216.00']].map(([when, detail]) => row(when, detail))}
            </div>
            <div className="mt-3" data-id="element-4299" data-mp="3aklic71r9til">{outlineButton('Download invoices')}</div>
          </>;
      case 'preferences':
        return <>
            {heading('Preferences')}
            <div className="mb-4" data-id="element-4300" data-mp="1va3md57vlgg9">
              {[['Name', 'Sarah Johnson'], ['Email', 'sarah@example.com'], ['Shipping address', '12 Vine St, Adelaide'], ['Payment method', 'Visa ending 4242'], ['Wine preference', 'Mixed reds']].map(([label, value]) => row(label, value))}
            </div>
            {filledButton('Save preferences')}
          </>;
      default:
        return <>
            {heading('Your collection')}
            <div className="grid grid-cols-3 gap-2.5 mb-4" data-id="element-4301" data-mp="1hgcm6ng1b8x7">
              {[['Shiraz 2021', 'Barossa Valley'], ['Grenache 2022', 'McLaren Vale'], ['Cabernet 2020', 'Coonawarra']].map(([name, meta]) => tile(name, meta))}
            </div>
            <div className="flex items-center gap-2" data-id="element-4302" data-mp="1r77adxcucila">
              {filledButton('Swap a bottle')}
              {outlineButton('Add to box')}
            </div>
          </>;
    }
  };
  const loyaltyBody = () => {
    switch (loyaltyPage) {
      case 'orders':
        return <>
            {heading('Your orders')}
            <div data-id="element-4303" data-mp="3bzl6x8u77yeq">
              {[['12 Jul 2026', '$142.00 · 320 points earned'], ['28 Apr 2026', '$96.00 · 215 points earned'], ['03 Feb 2026', '$210.00 · 470 points earned']].map(([when, detail]) => row(when, detail))}
            </div>
          </>;
      case 'support':
        return <>
            {heading('Support')}
            <p className="text-[13px] mb-3" style={{
            color: colors.text
          }} data-id="element-4304" data-mp="2t1ifkne6hpdr">
              Questions about your points, tier or an order? The cellar door team
              is here to help.
            </p>
            <div className="flex items-center gap-2" data-id="element-4305" data-mp="2k8f6sseha0pg">
              {filledButton('Contact the winery')}
              {outlineButton('Read FAQs')}
            </div>
          </>;
      case 'account':
        return <>
            {heading('Account')}
            <div className="mb-4" data-id="element-4306" data-mp="2ad55k6vwlxcq">
              {[['Name', 'Sarah Johnson'], ['Email', 'sarah@example.com'], ['Shipping address', '12 Vine St, Adelaide'], ['Marketing emails', 'Subscribed']].map(([label, value]) => row(label, value))}
            </div>
            {filledButton('Save details')}
          </>;
      default:
        return <>
            {heading('Rewards')}
            <div className="grid grid-cols-2 gap-2.5" data-id="element-4307" data-mp="flnrkbala4yb">
              {[['Free freight', '500 pts'], ['Bonus bottle', '1,500 pts'], ['Cellar door tasting', '2,000 pts'], ['Members-only magnum', '4,000 pts']].map(([name, cost]) => tile(name, cost))}
            </div>
          </>;
    }
  };
  const loyaltyHero = () => <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-4" data-id="element-4308" data-mp="3f3ds09cpfbuy">
        {statCard('Points Balance', '2,450 points', 'Worth $24.50')}
        {statCard('Current Tier', 'Sommelier', 'Next tier: Vintner')}
        {statCard('Qualifying Spend', '$1,250 of $2,000', '$750 to reach Vintner')}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2.5" data-id="element-4309" data-mp="18co111row0xd">
        {statCard('Qualifying Period Ends', '30 Jun 2027', 'Financial year window')}
        {statCard('Tier Held Until', '30 Jun 2027', 'Reviewed at period close')}
      </div>
    </>;
  const modalContent = () => {
    switch (modal) {
      case 'membership':
        return {
          title: 'Adjust membership',
          body: <div data-id="element-4310" data-mp="11wxkzl4uzgp7">
              {[['Frequency', 'Every 3 months'], ['Bottles per delivery', '6 bottles'], ['Next charge', '14 Aug 2026']].map(([label, value]) => row(label, value))}
            </div>,
          confirm: 'Update membership'
        };
      case 'reschedule':
        return {
          title: 'Reschedule delivery',
          body: <div className="flex items-center gap-2.5" data-id="element-4311" data-mp="1ku8k95qhqa4n">
              <Calendar className="w-5 h-5" style={{
              color: colors.primary
            }} data-id="element-4312" data-mp="1krcit7ffp3bg" />
              <span className="text-[13px]" style={{
              color: colors.text
            }} data-id="element-4313" data-mp="1pq74ch4sd5z9">
                Move 14 August 2026 to a new date
              </span>
            </div>,
          confirm: 'Confirm new date'
        };
      case 'pause':
        return {
          title: 'Pause membership',
          body: <p className="text-[13px] m-0" style={{
            color: colors.text
          }} data-id="element-4314" data-mp="2n5zya6k2y01b">
              Choose how long to pause. Your deliveries resume automatically.
            </p>,
          confirm: 'Pause membership'
        };
      case 'swap':
        return {
          title: 'Swap product',
          body: <div className="grid grid-cols-2 gap-2.5" data-id="element-4315" data-mp="296gxxzxifszk">
              {[['Shiraz 2021', 'In your box'], ['Grenache 2022', 'Swap in']].map(([name, meta]) => tile(name, meta))}
            </div>,
          confirm: 'Save swap'
        };
      case 'skip':
        return {
          title: 'Skip next delivery',
          body: <p className="text-[13px] m-0" style={{
            color: colors.text
          }} data-id="element-4316" data-mp="1ui4170qlm640">
              Your 14 August delivery will be skipped. The next one ships 14
              November 2026.
            </p>,
          confirm: 'Skip delivery'
        };
      case 'shipNow':
        return {
          title: 'Ship now',
          body: <p className="text-[13px] m-0" style={{
            color: colors.text
          }} data-id="element-4317" data-mp="10ga4jwmnzjp">
              Bring your next delivery forward and charge it today.
            </p>,
          confirm: 'Ship now'
        };
      case 'cancellation':
        return {
          title: 'Cancel membership',
          body: <p className="text-[13px] m-0" style={{
            color: colors.text
          }} data-id="element-4318" data-mp="evr29pjhm6kd">
              Before you go — tell us why you're leaving and see what we can
              offer.
            </p>,
          confirm: 'Continue'
        };
      default:
        return null;
    }
  };
  const activeModal = modalContent();
  const modalLabel = portalPreviewModals.find(item => item.id === modal)?.label ?? '';
  return <div className="relative" data-id="element-4319" data-mp="2upivphu2lvtf">
      <Region field="background" inspect={inspectMode} onInspect={onInspect} className="rounded-lg overflow-hidden" style={{
      backgroundColor: colors.background
    }} data-id="element-4320" data-mp="2uo7rxi0mko2b">
        {/* Top bar */}
        <div className="px-5 py-3 flex items-center justify-between" style={{
        borderBottom: `1px solid ${colors.border}`
      }} data-id="element-4321" data-mp="166ofsmrwz464">
          <Region field="text" inspect={inspectMode} onInspect={onInspect} data-id="element-4322" data-mp="114pedifqihbt">
            <span className="text-[14px] font-semibold" style={{
            color: colors.text
          }} data-id="element-4323" data-mp="1ezi4fat93fwn">
              Hillside Estate
            </span>
          </Region>
          {outlineButton('Back to My Account', 'text-[12px] px-3 py-1.5')}
        </div>

        {/* Hero banner */}
        {region('heroBackground', 'px-5 py-5', {
        background: heroFill
      }, <>
              <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-4324" data-mp="1xvhtoduug5cj">
                <div data-id="element-4325" data-mp="1tw931hv3onl7">
                  <div className="text-[20px] font-semibold" style={{
              color: colors.buttonText
            }} data-id="element-4326" data-mp="1tvpjxxtprt5l">
                    {loadedSubscriptionId ? `Membership ${loadedSubscriptionId}` : 'Welcome back, Sarah'}
                  </div>
                  <div className="text-[12px] mt-0.5" style={{
              color: colors.buttonText,
              opacity: 0.8
            }} data-id="element-4327" data-mp="3li44yapat1fd">
                    Sommelier member
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-4328" data-mp="3bs1cqcq03ri3">
                  {paused && <Region field="buttonText" inspect={inspectMode} onInspect={onInspect} className="rounded-full px-3 py-1 text-[12px] font-semibold" style={{
              backgroundColor: colors.buttonText,
              color: colors.heroBackground
            }} data-id="element-4329" data-mp="3grdy0t184qik">
                      Subscription paused
                    </Region>}
                  <Region field="buttonText" inspect={inspectMode} onInspect={onInspect} className="rounded-full px-3 py-1 text-[12px] font-semibold" style={{
              border: `1px solid ${colors.buttonText}`,
              color: colors.buttonText
            }} data-id="element-4330" data-mp="2svjerdkk6vde">
                    2,450 points
                  </Region>
                </div>
              </div>

              {isLoyaltyOnly ? loyaltyHero() : <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-4" data-id="element-4331" data-mp="tkouepvh57ww">
                    {statCard('Next Delivery', paused ? 'On hold' : '14 Aug 2026', paused ? 'Resumes when you unpause' : '6 bottles · Mixed reds')}
                    {statCard('Subscription', paused ? 'Paused' : 'Every 3 months', paused ? 'Paused since Jul 2026' : 'Active since Mar 2024')}
                    {statCard('Subscription Value', '$228.00', 'Per delivery')}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-4" data-id="element-4332" data-mp="18cpo6y71hzbx">
                    {heroButton('Adjust membership')}
                    {heroButton('Reschedule')}
                    {heroButton('Ship now')}
                  </div>
                </>}
            </>)}

        {/* Tab bar */}
        <div className="px-5 flex items-center gap-4 overflow-x-auto" style={{
        borderBottom: `1px solid ${colors.border}`
      }} data-id="element-4333" data-mp="1w2tey4z4q4li">
          {(isLoyaltyOnly ? portalLoyaltyPages : portalPreviewPages).map(item => {
          const active = item.id === (isLoyaltyOnly ? loyaltyPage : page);
          return <Region key={item.id} field={active ? 'primary' : 'text'} inspect={inspectMode} onInspect={onInspect} data-id="element-4334" data-mp="23l9q5lmqsm41">
                <span className="inline-block py-2.5 text-[12px] font-medium whitespace-nowrap" style={{
              color: active ? colors.primary : colors.text,
              opacity: active ? 1 : 0.6,
              borderBottom: active ? `2px solid ${colors.primary}` : '2px solid transparent'
            }} data-id="element-4335" data-mp="28mgxxkovxlap">
                  {item.label}
                </span>
              </Region>;
        })}
        </div>

        {/* Tab content */}
        <div className="p-5" data-id="element-4336" data-mp="2w9ym768xy18x">{isLoyaltyOnly ? loyaltyBody() : tabBody()}</div>
      </Region>

      {/* Modal flow preview */}
      {activeModal && <div className="absolute inset-0 z-20 flex items-start justify-center p-6 bg-black/40 rounded-lg overflow-auto" role="dialog" aria-label={`${modalLabel} preview`} data-id="element-4337" data-mp="316oydihmkcp2">
          <Region field="background" inspect={inspectMode} onInspect={onInspect} className="w-full max-w-[420px] rounded-lg shadow-lg overflow-hidden" style={{
        backgroundColor: colors.background
      }} data-id="element-4338" data-mp="3qk0jtu7nkkxc">
            <div className="px-4 py-3" style={{
          borderBottom: `1px solid ${colors.border}`
        }} data-id="element-4339" data-mp="4dc5cy4d2crh">
              <Region field="text" inspect={inspectMode} onInspect={onInspect} data-id="element-4340" data-mp="3vibto4xy6vfk">
                <h4 className="text-[15px] font-semibold m-0" style={{
              color: colors.text
            }} data-id="element-4341" data-mp="37vdlrvnw8vt9">
                  {activeModal.title}
                </h4>
              </Region>
            </div>
            <div className="p-4" data-id="element-4342" data-mp="3hmqbagm7z9w4">{activeModal.body}</div>
            <div className="px-4 py-3 flex items-center justify-end gap-2" style={{
          borderTop: `1px solid ${colors.border}`
        }} data-id="element-4343" data-mp="3csvt5hm9j2z5">
              {outlineButton('Close')}
              {filledButton(activeModal.confirm)}
            </div>
          </Region>
        </div>}
    </div>;
}